import {
  Lit3formBlock,
  html,
  unsafeHTML,
  classMap,
} from '@Root/lib/lit-3form-block';
import { styles } from './css';
import {
  analyticsCtaDataObject,
  adobeCleanString,
} from '../../../helpers/adobe-analytics';

import { vimeoIdFrom } from '@Helpers/vimeo-video';

export class Cory extends Lit3formBlock {
  static get characterName() {
    return 'cory';
  }

  static get characterProperties() {
    return {
      cta: { type: String },
    };
  }

  static get characterStyles() {
    return [...styles];
  }

  get spinshotId() {
    return this.content.spinshot_id || '';
  }

  get spinshotUrl() {
    return `https://embed.spinshot.io/${this.spinshotId}`;
  }

  render() {
    return html`
      <div class="block-wrapper">
        <div class="${classMap(this.layoutClasses)}">
          <div class="${classMap(this.detailsClasses)}">
            ${this.textBlockHTML} ${this.ctaHTML} ${this.downloadsHTML}
          </div>
          ${this.videoId
            ? html`
                <div class="video">
                  <vimeo-video
                    videoid="${this.videoId}"
                    fitaspect
                  ></vimeo-video>
                </div>
              `
            : this.imageHTML}
          ${this.horizontalRule}
        </div>
      </div>
    `;
  }

  get layoutClasses() {
    return {
      'block-grid': true,
      'cory-layout': true,
      // Avo persists this as the string '0' or '1', and '0' is truthy.
      mirror: this.content.mirror_layout == '1',
    };
  }

  get detailsClasses() {
    const verticalAlignment = this.content.vertical_text_alignment || 'top';
    const classes = {};
    classes['details'] = true;
    classes[`vertical-${verticalAlignment}`] = true;

    return classes;
  }

  get textBlockHTML() {
    if (!this.headerText && !this.descriptionText) return ``;

    return html`
      <div class="text-block">${this.headerHTML} ${this.descriptionHTML}</div>
    `;
  }

  get headerHTML() {
    return this.headerText
      ? html`
          <h2 class="header">${unsafeHTML(this.headerText)}</h2>
        `
      : ``;
  }

  get descriptionHTML() {
    return this.descriptionText
      ? html`
          <p class="description">${unsafeHTML(this.descriptionText)}</p>
        `
      : ``;
  }

  get ctaHTML() {
    return this.hasCta
      ? html`
          <cta-button
            class="cta-btn"
            cta-data=${this.ctaInfo}
            cta-type="secondary"
          ></cta-button>
        `
      : ``;
  }

  get downloadsHTML() {
    return this.hasDownloads
      ? html`
          <div class="downloads-wrapper">
            ${this.downloadsList.map(download => {
              return html`
                <cta-text
                  ctaData=${JSON.stringify(download)}
                  .ctaAnalyticsData=${analyticsCtaDataObject(
                    download,
                    this.headerText
                  )}
                  hide-right-arrow-svg="true"
                  target-blank="true"
                  left-download-svg="true"
                ></cta-text>
              `;
            })}
          </div>
        `
      : ``;
  }

  get ctaInfo() {
    const ctaData = this.content.cta || {};
    const cleanHeader = adobeCleanString(this.headerText);
    const cleanCtaText = adobeCleanString(ctaData.text);
    ctaData.analyticsName = `NAV|${cleanHeader}|${cleanCtaText}`;
    return JSON.stringify(ctaData);
  }

  get hasCta() {
    return this.content.cta && !!this.content.cta.text;
  }

  get hasDownloads() {
    return !!this.downloadsList.length;
  }

  get downloadsList() {
    return this.content.download_links || [];
  }

  get videoId() {
    return vimeoIdFrom(this.content.video_url);
  }

  get imageUrl() {
    return !!this.content.image
      ? this.imagebossUrl(this.content.image.src, null, 750)
      : '';
  }

  get fullResolutionImageUrl() {
    return !!this.content.image ? this.content.image.full_resolution_src : '';
  }

  get imageDescriptionHTML() {
    return !!this.imageDescription
      ? html`
          <image-caption
            class="image-description"
            description=${this.imageDescription}
            file-url=${this.fullResolutionImageUrl || ''}
            placement=${this.headerText}
          ></image-caption>
        `
      : html``;
  }

  get imageDescription() {
    return !!this.content.image?.description
      ? this.withLineBreaks(this.content.image.description)
      : '';
  }

  get imageHTML() {
    return !!this.spinshotId
      ? html`
          <iframe
            class="spinshot"
            src="${this.spinshotUrl}"
            frameborder="0"
            scrolling="no"
            style="background-color: transparent; border: 0;"
            allowfullscreen
          ></iframe>
        `
      : html`
          <div class="image">
            <img src="${this.imageUrl}" />
            ${this.imageDescriptionHTML}
          </div>
        `;
  }
}
Cory.defineElement();
